/**
 * 表セクションを正規形にする。保存パイプラインの入口で1回だけ呼ばれる。
 *
 * 正規形とは「固定行セルの内容が列と一致している」状態。
 * 編集中は固定行セルの内容を同期しないため、保存の前にここでまとめて列に揃える。
 *
 * セルと列の対応は現在の形の位置対応で取る。セル1つの正規化は「列サブツリーを id / uuid を
 * 引き継いで複製し、既存のセルサブツリーと丸ごと差し替える」だけ。差分判定は持たず
 * 常に作り直すが、同一性を引き継ぐため結果は変わらない(冪等)。
 */

import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { getCreateNodeId } from 'src/features/templateEdit/common/createNode'
import {
  collectSubtreeNodeIds,
  getRowCellQuestions,
  getTableColumnNodes,
  getTableFixedRowSectionNodes,
} from '../selector'
import { isTableSectionNode } from '../tableSection'
import { cloneColumnSubtree } from './cloneSubtree'
import { buildIdentityResolver } from './correspondence'
import { remapSubtreeQuestionRefs } from './refs'

/** 正規化の部分結果。差し替えるノード群と、置き換えで消える旧ノードの id。 */
type NormalizedCells = {
  upsertedNodes: TemplateNodesDict
  removedIds: number[]
}

const mergeNormalized = (results: NormalizedCells[]): NormalizedCells => ({
  upsertedNodes: Object.assign({}, ...results.map(r => r.upsertedNodes)),
  removedIds: results.flatMap(r => r.removedIds),
})

/** 固定行セル1つを列に揃える。 */
const normalizeCell = (
  columnNode: TemplateNodeSchema,
  cellNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
  allocateId: () => number,
  siblingUuidMap: ReadonlyMap<string, string>,
): NormalizedCells => {
  const resolveIdentity = buildIdentityResolver(
    columnNode,
    cellNode,
    templateNodes,
  )

  // 列サブツリーを id / uuid を引き継いで複製し、参照を行ローカルへ張り替える。
  const cloned = cloneColumnSubtree(
    columnNode,
    templateNodes,
    allocateId,
    resolveIdentity,
  )
  const rowUuidMap = new Map([...siblingUuidMap, ...cloned.uuidByColumnUuid])

  return {
    upsertedNodes: remapSubtreeQuestionRefs(cloned.nodes, rowUuidMap),
    removedIds: collectSubtreeNodeIds(cellNode.id, templateNodes),
  }
}

/** 固定行1行ぶんのセル群を列に揃える。列とセルは同じ index で対応する。 */
const normalizeRowCells = (
  row: TemplateNodeSchema,
  columnNodes: TemplateNodeSchema[],
  templateNodes: TemplateNodesDict,
  allocateId: () => number,
): NormalizedCells => {
  const rowCells = getRowCellQuestions(row, templateNodes)
  if (rowCells.length !== columnNodes.length) {
    throw new Error(
      `固定行(id: ${row.id})のセル数(${rowCells.length})が列数(${columnNodes.length})と一致しません`,
    )
  }
  const cellPairs = columnNodes.map((columnNode, index) => ({
    columnNode,
    cellNode: rowCells[index]!, // index は列数と同じなので必ず存在する。
  }))

  // 列セル uuid → この行の対応セル uuid。
  // 計算式は他の列も参照しうるため、全列の対応を先に揃えてから各セルを正規化する。
  const siblingUuidMap = new Map<string, string>()
  for (const { columnNode, cellNode } of cellPairs) {
    if (columnNode.uuid && cellNode.uuid) {
      siblingUuidMap.set(columnNode.uuid, cellNode.uuid)
    }
  }

  return mergeNormalized(
    cellPairs.map(({ columnNode, cellNode }) =>
      normalizeCell(
        columnNode,
        cellNode,
        templateNodes,
        allocateId,
        siblingUuidMap,
      ),
    ),
  )
}

/**
 * 全表の固定行セルの内容を列に揃える。id / uuid は位置対応で引き継ぐ。
 */
export const normalizeTableSections = (
  currentNodes: TemplateNodesDict,
): TemplateNodesDict => {
  const tableNodes = Object.values(currentNodes).filter(isTableSectionNode)
  if (tableNodes.length === 0) {
    return currentNodes
  }

  let nextNodeId = getCreateNodeId(currentNodes)
  const allocateId = () => nextNodeId++

  const { upsertedNodes, removedIds } = mergeNormalized(
    tableNodes.flatMap(tableNode => {
      const columnNodes = getTableColumnNodes(tableNode, currentNodes)
      return getTableFixedRowSectionNodes(tableNode, currentNodes).map(row =>
        normalizeRowCells(row, columnNodes, currentNodes, allocateId),
      )
    }),
  )

  // 削除を除外し、追加・更新を重ねた新辞書を返す。
  const removed = new Set(removedIds)
  const result: TemplateNodesDict = {}
  for (const [id, node] of Object.entries(currentNodes)) {
    if (!removed.has(Number(id))) {
      result[Number(id)] = node
    }
  }
  return { ...result, ...upsertedNodes }
}
